import { LibraryBigIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isMessageFromKnowledge } from '@/modules/tryIt/utils/helper';

import { intentRow } from './AmendRows';
import {
  buildNodeRow,
  buildStepRows,
  buildTextSentRow,
  buildUnderstandRows,
  useAfterAnswerRows,
} from './buildStepRows';
import { DetailRowSpec, DetailRows, PlainRow } from './DetailsRow';
import { useKnowledgeBaseRows } from './DocumentRows';
import { getIntentInfo } from './IntentDetail';
import { buildToolRows } from './ToolsRows';
import { ConversationTurn } from '../../../types/conversation';
import { isCollectionMessage } from '../../../utils/utils';

type GuidelinesLLMDetailsProps = { turn: ConversationTurn; grouped?: boolean };

/** Guidelines / LLM variant: rephrase → intent → multiple sources → agent knowledge → KB → sources. */
export const GuidelinesLLMDetails = ({ turn, grouped }: GuidelinesLLMDetailsProps) => {
  const { t } = useTranslation();

  const intentInfo = getIntentInfo(turn);
  const answerRows = useAfterAnswerRows(turn);
  const knowledgeBaseRows = useKnowledgeBaseRows(turn);
  const isFromKnowledge = isMessageFromKnowledge(turn.metadata.collection);
  const hasCollection = isCollectionMessage(turn.metadata.collection);
  // Build the timeline declaratively: optional rows are `false` and dropped by
  // DetailRows, so the fixed-vs-conditional structure reads top-to-bottom.
  const rows: (DetailRowSpec | false | null)[] = [
    ...buildUnderstandRows(t, turn),
    intentInfo.hasInfo && { ...intentRow(turn), phase: 'understand' as const },
    buildNodeRow(t, turn),
    isFromKnowledge &&
      hasCollection && {
        key: 'sources',
        icon: <LibraryBigIcon className="h-4 w-4" />,
        head: <PlainRow>{t('try_it.details.multiple_sources')}</PlainRow>,
      },
    isFromKnowledge && {
      key: 'knowledge',
      icon: <ZapIcon className="h-4 w-4" />,
      head: <PlainRow>{t('try_it.details.agents_knowledge_found')}</PlainRow>,
    },
    ...knowledgeBaseRows,
    ...buildToolRows(turn, 'agent'),
    ...buildStepRows(t, turn, answerRows),
    buildTextSentRow(t, turn),
  ];

  return <DetailRows rows={rows} grouped={grouped} />;
};
